import {
  fetchAuctionPickups,
  fetchHouseContact,
  fetchLotPhotos,
  houseLabel,
  invoiceIsOutstanding,
  invoiceStatusLabel,
  invoiceStatusTone,
  resolvePickup,
  totalIsAmountOwed,
  type HouseContact,
  type ResolvedPickup,
} from '@auctisync/api-client';
import { Button, LearnMoreLink } from '@auctisync/ui';
import { formatCents, thumbUrl, type Tables } from '@auctisync/shared';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { BidderOfflineDeclare } from '../../components/BidderOfflineDeclare';
import { useBidderHouses } from '../../hooks/useBidderHouses';
import { useSession } from '../../hooks/useSession';
import { invokeEF } from '../../lib/edgeFunctions';
import { friendlyPaymentError } from '../../lib/paymentErrors';
import { loadStripeForAccount, missingStripeKeyMessage, stripeConfigured } from '../../lib/stripe';
import { supabase } from '../../lib/supabase';

type Invoice = Tables<'invoices'>;
type Line = Tables<'invoice_line_items'>;
type Auction = Pick<Tables<'auctions'>, 'id' | 'title' | 'slug'>;
type InvoiceWithAuction = Invoice & { auctions: Auction | null };
type SavedCard = {
  id: string;
  brand: string;
  last4: string;
  exp_month: number | null;
  exp_year: number | null;
  is_default: boolean;
};

/**
 * /my/invoices/:id — invoice detail with Pay Now flow.
 *
 * REBUILT to the web design handoff (`web/web-account.jsx`) on 2026-09-08. It
 * was one column of stacked cards: a six-column table, a summary card repeating
 * the same figures, and the pay control LAST — below the fold on anything but a
 * tall screen, on the page whose entire job is to get an invoice paid.
 *
 * 🔴 THE PAYMENT LOGIC IS UNTOUCHED. Every branch below is the code that was
 * already here — the pay-lock, the saved-card one-click path with its in-place
 * 3DS, the new-card modal, the offline declare, the paid state. This change is
 * about WHERE they sit, and a redesign of a money page that quietly rewrote one
 * of them would be the worst trade in the product.
 *
 * ⚠️ THE PER-LINE BREAKDOWN SURVIVED, against the design. The handoff shows a
 * hammer price per lot and the premium once at the bottom, because its mock had
 * a flat 13% and no tax. Real invoices do not: `lots.tax_exempt` and per-sale
 * tax profiles mean two lines on the SAME invoice can carry different tax. So
 * each row keeps hammer + premium + tax underneath it — dropping it would make
 * an invoice that cannot be reconciled against what was actually charged.
 *
 * WHAT MOVED WHERE. The right column is the design's amount-due panel and the
 * collection band: the happy path, and the question a bidder asks straight
 * after paying. The offline-payment form stayed on the LEFT — it is a form with
 * radio buttons and it does not belong squeezed into a 320px rail.
 *
 * Payment flow:
 *  1. Page loads; if status is unpayable, shows read-only detail.
 *  2. User clicks "Pay now". We call the create-invoice-payment-intent
 *     Edge Function to get a { client_secret, stripe_account_id }.
 *  3. We load Stripe.js scoped to the tenant's Connect account, wrap
 *     <PaymentElement> in <Elements>, and let the user confirm.
 *  4. Stripe webhook flips the invoice to 'paid' when the PI succeeds.
 *
 * Money-safety notes (adversarial-review fixes):
 *  - Card pay is offered ONLY while status === 'issued'. partially_paid is
 *    excluded until balance tracking exists — charging total_cents against a
 *    partially-paid invoice would overcharge (finding #3).
 *  - Once a payment is confirmed client-side (or the server reports one is
 *    already in flight), the ENTIRE pay section is replaced with a calm
 *    "processing" panel and we poll for the webhook's paid-flip. This closes
 *    the double-pay window where an impatient bidder pays twice (finding #5).
 *  - Saved-card 3DS challenges complete in place via confirmCardPayment on
 *    the tenant-scoped Stripe instance — we do NOT ask the bidder to re-enter
 *    the card in the new-card form (findings #6/#11).
 */
export function BidderInvoiceDetail() {
  const { id } = useParams();
  const { session, loading: sessionLoading, userId } = useSession();

  const [invoice, setInvoice] = useState<InvoiceWithAuction | null>(null);
  const [lines, setLines] = useState<Line[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [payOpen, setPayOpen] = useState(false);
  const [payBusy, setPayBusy] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [stripeAccountId, setStripeAccountId] = useState<string | null>(null);

  // Saved-card one-click path.
  const [savedCards, setSavedCards] = useState<SavedCard[]>([]);
  const [selectedPm, setSelectedPm] = useState<string | null>(null);
  const [useNewCard, setUseNewCard] = useState(false);
  const [saveNewCard, setSaveNewCard] = useState(false);
  const [oneClickBusy, setOneClickBusy] = useState(false);
  const [acceptedOffline, setAcceptedOffline] = useState<{ cash: boolean; check: boolean }>({
    cash: false,
    check: false,
  });
  // Tenant toggle for online card payments (defaults ON when never configured).
  const [cardOnline, setCardOnline] = useState(true);

  // Pay-lock (finding #5): once a payment is confirmed (or the server says one
  // is already in flight) we hide every pay control and wait for the webhook.
  const [paymentPending, setPaymentPending] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const pollCountRef = useRef(0);

  // A photograph per line, and where to collect. Both are presentation-only:
  // they load in a second wave and a failure costs a picture or a band, never
  // the invoice or its pay button.
  const [photos, setPhotos] = useState<ReadonlyMap<string, { url: string }[]>>(new Map());
  const [pickup, setPickup] = useState<ResolvedPickup | null>(null);
  const [house, setHouse] = useState<HouseContact | null>(null);
  const { selected } = useBidderHouses();

  // `silent` refreshes skip the full-page loader — used by the pay-lock
  // poller and post-payment refreshes so the page doesn't flash every tick.
  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!id || userId === null) return;
      if (!opts?.silent) setLoading(true);
      const [{ data: invRow, error: invErr }, { data: lineRows, error: lineErr }] =
        await Promise.all([
          supabase
            .from('invoices')
            .select('*, auctions!invoices_auction_id_fkey(id, title, slug)')
            .eq('id', id)
            .maybeSingle(),
          supabase
            .from('invoice_line_items')
            .select('*')
            .eq('invoice_id', id)
            .order('sort_order', { ascending: true }),
        ]);
      if (invErr) setError(invErr.message);
      if (lineErr) setError(lineErr.message);
      setInvoice((invRow ?? null) as InvoiceWithAuction | null);
      setLines((lineRows ?? []) as Line[]);
      setLoading(false);
    },
    [id, userId],
  );

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Photographs, collection details and the house — the presentational second
   * wave.
   *
   * Deliberately separate from `load`: that one is on a poller while a payment
   * settles, and re-reading four more things every tick to redraw a photograph
   * that has not changed would be paying for nothing.
   */
  const auctionId = invoice?.auction_id;
  const tenantId = invoice?.tenant_id;
  useEffect(() => {
    if (auctionId === undefined || tenantId === undefined) return;
    let cancelled = false;
    void (async () => {
      const [pickups, contact] = await Promise.all([
        fetchAuctionPickups(supabase, [auctionId]),
        fetchHouseContact(supabase, tenantId),
      ]);
      if (cancelled) return;
      setHouse(contact);
      setPickup(
        resolvePickup({
          auction: pickups.get(auctionId) ?? null,
          invoiceSummary: invoice?.pickup_summary ?? null,
          house: contact,
        }),
      );
    })();
    return () => {
      cancelled = true;
    };
    // Keyed on the ids only. `pickup_summary` is read inside but cannot change
    // without the invoice id changing, and the invoice id remounts this page —
    // adding `invoice` here would re-run all of this on every payment poll.
  }, [auctionId, tenantId, invoice?.pickup_summary]);

  useEffect(() => {
    const withLots = lines.filter((l) => l.lot_id !== null);
    if (withLots.length === 0) return;
    let cancelled = false;
    void (async () => {
      // The SAME resolver every other surface uses, so an invoice cannot show a
      // different picture for a lot than the lot page did.
      const map = await fetchLotPhotos(
        supabase,
        withLots.map((l) => ({ id: l.lot_id, inventory_item_id: null })),
      );
      if (!cancelled) setPhotos(map);
    })();
    return () => {
      cancelled = true;
    };
  }, [lines]);

  // Card-payable ONLY while 'issued'. partially_paid pays offline until
  // balance tracking exists — total_cents would overcharge (finding #3).
  const canPayCard = useMemo(() => invoice?.status === 'issued', [invoice]);
  // Offline declare stays available for partially_paid too — it declares
  // INTENT to pay in person, it doesn't move money.
  const canDeclareOffline = useMemo(
    () => invoice?.status === 'issued' || invoice?.status === 'partially_paid',
    [invoice],
  );

  // Enter the pay-lock: hide all pay controls and let the poller below watch
  // for the webhook's status flip. `message` overrides the default copy (used
  // for the "already in progress / already paid" 409s — calm, not an error).
  const beginPending = useCallback((message: string | null) => {
    pollCountRef.current = 0;
    setError(null);
    setPendingMessage(message);
    setPaymentPending(true);
    setPayOpen(false);
  }, []);

  // While the pay-lock is on and the invoice hasn't flipped yet, poll load()
  // every 2.5s up to 6 times. If the webhook is slower than that, the panel
  // stays up (safe: pay controls remain hidden) and a refresh re-syncs.
  useEffect(() => {
    if (!paymentPending) return;
    if (invoice && invoice.status !== 'issued') {
      // Status flipped (webhook landed) — drop the lock so the normal
      // status-driven UI (paid banner, etc.) takes over.
      setPaymentPending(false);
      return;
    }
    const t = setInterval(() => {
      pollCountRef.current += 1;
      if (pollCountRef.current > 6) {
        clearInterval(t);
        return;
      }
      void load({ silent: true });
    }, 2500);
    return () => clearInterval(t);
  }, [paymentPending, invoice, load]);

  // Complete a 3DS/SCA challenge in place on the tenant-scoped Stripe
  // instance (findings #6/#11 — never make the bidder re-enter the card).
  // Returns true when the bank check passed and the pay-lock is engaged.
  const complete3ds = useCallback(
    async (secret: string, accountId: string): Promise<boolean> => {
      try {
        const stripe = await loadStripeForAccount(accountId);
        if (!stripe) {
          setError(missingStripeKeyMessage());
          return false;
        }
        const res = await stripe.confirmCardPayment(secret);
        if (res.error) {
          setError(
            res.error.message ??
              'Your bank could not verify this payment. You can try a different card below.',
          );
          return false;
        }
        beginPending(null);
        return true;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return false;
      }
    },
    [beginPending],
  );

  const startPayment = useCallback(async () => {
    if (!invoice) return;
    if (!stripeConfigured()) {
      setError(missingStripeKeyMessage());
      return;
    }
    setPayBusy(true);
    setError(null);
    const { data, error: fnErr } = await invokeEF<{
      client_secret?: string;
      stripe_account_id?: string;
      pi_status?: string;
      already_paid?: boolean;
    }>('create-invoice-payment-intent', { invoice_id: invoice.id, save_card: saveNewCard });
    if (fnErr) {
      setPayBusy(false);
      // The server pre-check says a payment already exists — not an error for
      // the bidder, just wait for the page to catch up (finding #5).
      if (fnErr === 'payment_in_progress' || fnErr === 'payment_already_succeeded') {
        beginPending(friendlyPaymentError(fnErr));
        return;
      }
      setError(friendlyPaymentError(fnErr));
      return;
    }
    if (data?.already_paid) {
      // Invoice was paid between page load and the click — refresh; the paid
      // banner takes over.
      await load({ silent: true });
      setPayBusy(false);
      return;
    }
    if (!data?.client_secret || !data?.stripe_account_id) {
      setPayBusy(false);
      setError('Could not start payment.');
      return;
    }
    // SCA recovery: the server reused an existing PI that's stuck waiting on a
    // bank check. Finish that check directly — the PI already has the card.
    if (data.pi_status === 'requires_action') {
      await complete3ds(data.client_secret, data.stripe_account_id);
      setPayBusy(false);
      return;
    }
    setClientSecret(data.client_secret);
    setStripeAccountId(data.stripe_account_id);
    setPayOpen(true);
    setPayBusy(false);
  }, [invoice, saveNewCard, beginPending, complete3ds, load]);

  // Load the bidder's saved cards for this house once the invoice is card-payable.
  useEffect(() => {
    if (!invoice || invoice.status !== 'issued') return;
    let cancelled = false;
    void (async () => {
      const { data } = await invokeEF<{
        cards: SavedCard[];
        default_payment_method: string | null;
      }>('list-payment-methods', { tenant_id: invoice.tenant_id });
      if (cancelled || !data) return;
      setSavedCards(data.cards);
      // The stored default can point at a detached card (finding #16) — only
      // preselect it when it's actually in the returned list.
      const def = data.default_payment_method;
      setSelectedPm(
        def && data.cards.some((c) => c.id === def) ? def : (data.cards[0]?.id ?? null),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [invoice]);

  // Tenant's accepted methods: cash/check for the pay-in-person option, plus
  // the card_online kill-switch (finding #10 — hide card pay when the house
  // turned it off; absent settings mean ON for back-compat).
  useEffect(() => {
    if (!invoice) return;
    if (invoice.status !== 'issued' && invoice.status !== 'partially_paid') return;
    let cancelled = false;
    void (async () => {
      const { data } = await supabase.rpc('get_invoice_print_context', {
        p_invoice_id: invoice.id,
      });
      if (cancelled) return;
      const settings = (data?.[0]?.invoice_settings ?? {}) as Record<string, unknown>;
      const am = (settings.accepted_methods ?? {}) as {
        card_online?: boolean;
        cash?: boolean;
        check?: boolean;
      };
      setAcceptedOffline({ cash: am.cash === true, check: am.check === true });
      setCardOnline(am.card_online !== false);
    })();
    return () => {
      cancelled = true;
    };
  }, [invoice]);

  const oneClickPay = useCallback(async () => {
    if (!invoice || !selectedPm) return;
    setOneClickBusy(true);
    setError(null);
    const { data, error: err } = await invokeEF<{
      ok?: boolean;
      already_paid?: boolean;
      requires_action?: boolean;
      client_secret?: string;
      stripe_account_id?: string;
    }>('charge-new-card', {
      invoice_id: invoice.id,
      payment_method_id: selectedPm,
    });
    if (err) {
      setOneClickBusy(false);
      // A payment already exists for this invoice — engage the pay-lock
      // instead of erroring (finding #5).
      if (err === 'payment_in_progress' || err === 'payment_already_succeeded') {
        beginPending(friendlyPaymentError(err));
        return;
      }
      setError(friendlyPaymentError(err));
      return;
    }
    if (data?.already_paid) {
      // Server says it's paid — just refresh; the paid banner takes over.
      await load({ silent: true });
      setOneClickBusy(false);
      return;
    }
    if (data?.requires_action) {
      if (data.client_secret && data.stripe_account_id) {
        // 3DS challenge on the SAVED card — complete it right here (findings
        // #6/#11); only offer the new-card form if the bank check fails. The
        // button stays busy through the challenge so a second click can't
        // start a competing charge mid-verification.
        const completed = await complete3ds(data.client_secret, data.stripe_account_id);
        setOneClickBusy(false);
        if (!completed) setUseNewCard(true);
      } else {
        setOneClickBusy(false);
        // Response missing the challenge secret (shouldn't happen with the
        // current server) — fall back to the secure form, NOT the pay-lock.
        setUseNewCard(true);
        setError(
          'Your bank needs to verify this payment. Please complete it with the secure form below.',
        );
      }
      return;
    }
    setOneClickBusy(false);
    // Charge accepted (succeeded or processing) — lock the pay section and
    // wait for the webhook to flip the invoice (finding #5).
    beginPending(null);
  }, [invoice, selectedPm, load, beginPending, complete3ds]);

  if (sessionLoading) return <p className="muted">Loading…</p>;
  // `/admin` is the STAFF sign-in. A bidder sent there sees a login form for a
  // product they have no account in — the same bug the lot page carried until
  // 2026-09-03.
  if (!session) return <Navigate to="/signin" replace />;
  if (loading) return <p className="muted">Loading invoice…</p>;
  if (!invoice) {
    return (
      <>
        <p style={{ color: 'var(--semantic-error)' }}>Invoice not found.</p>
        <Link to="/my/invoices" className="w-btn w-btn-out">
          All invoices
        </Link>
      </>
    );
  }

  const statusLabel = invoiceStatusLabel(invoice.status);
  const isPaid = invoiceStatusTone(invoice.status) === 'paid';
  // `total_cents` is the amount OWED only for statuses where nothing has been
  // paid against it yet. On a partially-paid invoice it is the original bill,
  // not the balance — which is also why card pay is withheld there.
  const amountDue = totalIsAmountOwed(invoice.status) ? invoice.total_cents : null;
  const houseName = selected === null ? 'the auction house' : houseLabel(selected);
  // Only when the paddle belongs to THIS invoice's house. A bidder registered
  // with two houses has two numbers, and showing the wrong one at a collection
  // desk is worse than showing none.
  const paddle =
    selected !== null && selected.tenant_id === invoice.tenant_id
      ? selected.tenant_bidder_number
      : null;

  return (
    <>
      <Link
        to="/my/invoices"
        className="w-btn w-btn-quiet"
        style={{ padding: 0, minHeight: 40, marginBottom: 14 }}
      >
        ‹ All invoices
      </Link>

      <div className="wa-hd">
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 8,
              flexWrap: 'wrap',
            }}
          >
            <span className="wa-stamp" data-paid={isPaid}>
              {statusLabel}
            </span>
            {invoice.due_at && !isPaid ? (
              <span className="w-lbl">
                Due{' '}
                {new Date(invoice.due_at).toLocaleDateString(undefined, {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                })}
              </span>
            ) : null}
            {invoice.paid_at && isPaid ? (
              <span className="w-lbl">Paid {new Date(invoice.paid_at).toLocaleDateString()}</span>
            ) : null}
          </div>
          <h1 className="w-h1" style={{ fontSize: 30 }}>
            Invoice {invoice.invoice_number ?? ''}
          </h1>
          <p className="muted" style={{ fontSize: 14, marginTop: 6 }}>
            {invoice.auctions?.title ?? 'Auction'}
            {invoice.issued_at
              ? ` · Issued ${new Date(invoice.issued_at).toLocaleDateString()}`
              : ''}
          </p>
        </div>
        <Link
          to={`/my/invoices/${invoice.id}/print`}
          target="_blank"
          rel="noopener"
          className="w-btn w-btn-out w-btn-sm"
        >
          Print / Save as PDF
        </Link>
      </div>

      {error && (
        <div role="alert" style={errorStyle}>
          {error}
        </div>
      )}

      <div className="wa-inv">
        {/* ── what you bought ── */}
        <div>
          <div className="wa-card" style={{ marginBottom: 20 }}>
            {lines.map((l) => {
              const photo = l.lot_id === null ? null : (photos.get(l.lot_id)?.[0]?.url ?? null);
              return (
                <div className="wa-row" key={l.id}>
                  <span
                    className="ph"
                    style={{ width: 52, height: 52, borderRadius: 9, flexShrink: 0 }}
                  >
                    {photo === null ? null : <img src={thumbUrl(photo)} alt="" loading="lazy" />}
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span className="w-lbl" style={{ display: 'block' }}>
                      Lot {l.lot_number}
                    </span>
                    <span style={{ fontSize: 14.5, fontWeight: 550 }}>{l.description}</span>
                    {/* 🔴 KEPT, against the design's simpler row. Per-lot tax
                        genuinely varies (`lots.tax_exempt`, per-sale tax
                        profiles), so an invoice without this cannot be
                        reconciled against what was charged. */}
                    <span
                      className="muted mono"
                      style={{ display: 'block', fontSize: 11.5, marginTop: 3 }}
                    >
                      {formatCents(l.hammer_cents)} hammer + {formatCents(l.buyers_premium_cents)}{' '}
                      premium
                      {l.sales_tax_cents > 0 ? ` + ${formatCents(l.sales_tax_cents)} tax` : ''}
                    </span>
                  </span>
                  <span className="w-price" style={{ fontSize: 16 }}>
                    {formatCents(l.line_total_cents)}
                  </span>
                </div>
              );
            })}
            {lines.length === 0 ? (
              <div className="wa-row">
                <span className="muted">This invoice has no line items.</span>
              </div>
            ) : null}
          </div>

          <div className="wa-card" style={{ padding: '4px 18px' }}>
            <div className="w-kv">
              <span className="muted">Hammer total</span>
              <span className="mono">{formatCents(invoice.subtotal_cents)}</span>
            </div>
            <div className="w-kv">
              <span className="muted">Buyer&apos;s premium</span>
              <span className="mono">{formatCents(invoice.buyers_premium_cents)}</span>
            </div>
            {invoice.online_premium_cents > 0 ? (
              <div className="w-kv">
                <span className="muted">Online premium</span>
                <span className="mono">{formatCents(invoice.online_premium_cents)}</span>
              </div>
            ) : null}
            {invoice.sales_tax_cents > 0 ? (
              <div className="w-kv">
                <span className="muted">Sales tax</span>
                <span className="mono">{formatCents(invoice.sales_tax_cents)}</span>
              </div>
            ) : null}
            {invoice.shipping_cents > 0 ? (
              <div className="w-kv">
                <span className="muted">Shipping</span>
                <span className="mono">{formatCents(invoice.shipping_cents)}</span>
              </div>
            ) : null}
            <div className="w-kv" style={{ fontSize: 17, fontWeight: 700 }}>
              <span>Total</span>
              <span className="w-price">{formatCents(invoice.total_cents)}</span>
            </div>
          </div>

          {/* PAYING IN PERSON STAYS ON THE LEFT. It is a form with radio buttons
              and a reference field; squeezed into a 320px rail it becomes the
              hardest way to do the easiest thing. */}
          {canDeclareOffline &&
            !paymentPending &&
            (acceptedOffline.cash || acceptedOffline.check) && (
              <div style={{ marginTop: 20 }}>
                <BidderOfflineDeclare
                  invoiceId={invoice.id}
                  tenantId={invoice.tenant_id}
                  acceptedMethods={acceptedOffline}
                  onDeclared={() => {
                    void load({ silent: true });
                  }}
                />
              </div>
            )}

          {invoice.notes ? (
            <p className="muted" style={{ fontSize: 13, marginTop: 18, lineHeight: 1.6 }}>
              {invoice.notes}
            </p>
          ) : null}
        </div>

        {/* ── what you owe, and where you collect ── */}
        <aside className="wa-inv-side">
          <div
            className="wa-card"
            style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 13 }}
          >
            <div>
              <div className="w-lbl">{isPaid ? 'Paid in full' : 'Amount due'}</div>
              <div className="w-price" style={{ fontSize: 30, marginTop: 3 }}>
                {isPaid ? formatCents(0) : formatCents(amountDue ?? invoice.total_cents)}
              </div>
              {amountDue === null && !isPaid ? (
                <p className="muted" style={{ fontSize: 12, margin: '6px 0 0', lineHeight: 1.5 }}>
                  Part of this invoice has been paid. {houseName} will confirm the remaining balance
                  — it is not shown here rather than shown wrong.
                </p>
              ) : null}
            </div>

            {/* Pay-lock (finding #5): replaces every pay control while we wait
                for the webhook to flip the invoice status. */}
            {paymentPending ? (
              <div
                role="status"
                style={{
                  padding: '12px 14px',
                  borderRadius: 'var(--r-control)',
                  background: 'var(--surface-muted)',
                  border: '1px solid var(--border)',
                  fontSize: 13.5,
                  lineHeight: 1.5,
                }}
              >
                {pendingMessage ?? 'Payment processing — this page will update automatically.'}
              </div>
            ) : isPaid ? (
              <div
                style={{
                  display: 'flex',
                  gap: 10,
                  alignItems: 'center',
                  padding: '12px 14px',
                  borderRadius: 'var(--r-control)',
                  background:
                    'color-mix(in oklab, var(--semantic-winning) 12%, var(--surface-elev))',
                  border: '1px solid color-mix(in oklab, var(--semantic-winning) 34%, transparent)',
                }}
              >
                <span style={{ color: 'var(--semantic-winning)' }} aria-hidden>
                  ✓
                </span>
                <span style={{ fontSize: 13.5, fontWeight: 600 }}>
                  Paid{invoice.paid_at ? ` ${new Date(invoice.paid_at).toLocaleDateString()}` : ''}.
                  Bring ID to collect.
                </span>
              </div>
            ) : canPayCard && cardOnline && !payOpen ? (
              savedCards.length > 0 && !useNewCard ? (
                <>
                  {savedCards.map((c) => (
                    <label key={c.id} style={savedCardRowStyle}>
                      <input
                        type="radio"
                        name="savedcard"
                        checked={selectedPm === c.id}
                        onChange={() => setSelectedPm(c.id)}
                      />
                      <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>
                        {c.brand}
                      </span>
                      <span style={{ fontFamily: 'var(--font-mono, monospace)' }}>
                        •••• {c.last4}
                      </span>
                      {c.is_default ? <span className="tag t-nr">Default</span> : null}
                    </label>
                  ))}
                  <button
                    type="button"
                    className="w-btn w-btn-cta w-btn-lg w-btn-full"
                    disabled={oneClickBusy || !selectedPm}
                    onClick={() => {
                      void oneClickPay();
                    }}
                  >
                    {oneClickBusy ? 'Processing…' : `Pay ${formatCents(invoice.total_cents)}`}
                  </button>
                  <button
                    type="button"
                    onClick={() => setUseNewCard(true)}
                    disabled={oneClickBusy}
                    style={{
                      ...linkBtnStyle,
                      ...(oneClickBusy ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                    }}
                  >
                    Use a different card
                  </button>
                  <p className="muted" style={{ fontSize: 12, margin: 0, lineHeight: 1.5 }}>
                    Charged to the card on file. Pay before you collect and the counter is just an
                    ID check.
                  </p>
                </>
              ) : (
                <>
                  {savedCards.length > 0 ? (
                    <button
                      type="button"
                      onClick={() => setUseNewCard(false)}
                      disabled={payBusy}
                      style={{
                        ...linkBtnStyle,
                        ...(payBusy ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                      }}
                    >
                      ← Back to saved cards
                    </button>
                  ) : null}
                  <label
                    style={{
                      display: 'flex',
                      gap: '0.5rem',
                      alignItems: 'flex-start',
                      fontSize: '0.85rem',
                      color: 'var(--text)',
                      lineHeight: 1.45,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={saveNewCard}
                      onChange={(e) => setSaveNewCard(e.target.checked)}
                    />
                    Save this card with the auction house for faster checkout next time
                  </label>
                  {saveNewCard && (
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: 0 }}>
                      By saving, you authorize the auction house to charge this card for future
                      invoices on lots you win. You can remove it anytime from Payment methods.
                    </p>
                  )}
                  <button
                    type="button"
                    className="w-btn w-btn-cta w-btn-lg w-btn-full"
                    disabled={payBusy}
                    onClick={() => {
                      void startPayment();
                    }}
                  >
                    {payBusy
                      ? 'Opening secure payment…'
                      : `Pay ${formatCents(invoice.total_cents)}`}
                  </button>
                  <LearnMoreLink helpSlug="paying-your-invoice" newTab>
                    How invoice payments work
                  </LearnMoreLink>
                </>
              )
            ) : canDeclareOffline && (acceptedOffline.cash || acceptedOffline.check) ? (
              <p className="muted" style={{ fontSize: 13, margin: 0, lineHeight: 1.55 }}>
                {houseName} takes payment in person for this sale. Declare how you intend to pay
                using the form beside this.
              </p>
            ) : invoiceIsOutstanding(invoice.status) ? (
              <p className="muted" style={{ fontSize: 13, margin: 0, lineHeight: 1.55 }}>
                Contact {houseName} to arrange payment for this invoice.
              </p>
            ) : (
              /* Draft, refunded or cancelled. "Contact them to arrange payment"
                 would be wrong on all three — nothing is owed. The status line
                 above already says which, so this says nothing further rather
                 than inventing an instruction. */
              <p className="muted" style={{ fontSize: 13, margin: 0, lineHeight: 1.55 }}>
                Nothing is payable on this invoice.
              </p>
            )}
          </div>

          {/*
            THE COLLECTION BAND — the inverted brand plate, never red. Pickup is
            not an alarm, and red is locked to the auction semantics (closing,
            outbid); a red plate here would teach a bidder to read "collection"
            as "something has gone wrong".

            🔴 NOTHING IN IT IS INVENTED. The design's mock reads "Saturday 30
            August, 9am–4pm ... lots not collected within 14 days are resold",
            because it was drawn for one house on one day. We hold no resale
            term and, until the auction-creation scheduler writes
            `auctions.pickup_dates`, no absolute date either. So the band shows
            what `resolvePickup` actually resolved and says plainly when that is
            nothing.
          */}
          <div className="wa-band">
            <div className="w-lbl">Collection</div>
            <div
              style={{
                fontFamily: 'var(--font-heading)',
                fontSize: 17,
                fontWeight: 700,
                marginTop: 4,
              }}
            >
              {pickup?.window ?? 'Collection times not published yet'}
            </div>
            {(pickup?.address ?? pickup?.location) ? (
              <p
                style={{
                  fontSize: 13,
                  margin: '6px 0 0',
                  color: 'var(--text-label-on-deep)',
                  lineHeight: 1.5,
                }}
              >
                {[pickup.location, pickup.address].filter(Boolean).join(' · ')}
              </p>
            ) : null}
            <p
              style={{
                fontSize: 13,
                margin: '6px 0 0',
                color: 'var(--text-label-on-deep)',
                lineHeight: 1.5,
              }}
            >
              Collection only — nothing is shipped. Bring photo ID
              {paddle === null ? '' : ` and bidder number ${String(paddle)}`}.
            </p>
            {pickup?.instructions ? (
              <p
                style={{
                  fontSize: 13,
                  margin: '6px 0 0',
                  color: 'var(--text-label-on-deep)',
                  lineHeight: 1.5,
                }}
              >
                {pickup.instructions}
              </p>
            ) : null}
            {pickup !== null && pickup.from === 'none' ? (
              <p
                style={{
                  fontSize: 13,
                  margin: '6px 0 0',
                  color: 'var(--text-label-on-deep)',
                  lineHeight: 1.5,
                }}
              >
                {houseName} has not published collection details for this sale. Your lots are held
                for you — ask them and the answer appears here.
              </p>
            ) : null}
            {house?.phone ? (
              <a
                href={`tel:${house.phone}`}
                className="w-btn w-btn-ghost w-btn-sm"
                style={{ marginTop: 14 }}
              >
                {house.phone}
              </a>
            ) : null}
          </div>
        </aside>
      </div>

      {payOpen && clientSecret && stripeAccountId && (
        <PaymentModal
          clientSecret={clientSecret}
          stripeAccountId={stripeAccountId}
          amountLabel={formatCents(invoice.total_cents)}
          invoiceNumber={invoice.invoice_number ?? ''}
          onClose={() => setPayOpen(false)}
          onSuccess={() => {
            // Card confirmed — engage the pay-lock and poll for the webhook
            // flip rather than re-exposing the pay buttons (finding #5).
            beginPending(null);
            void load({ silent: true });
          }}
        />
      )}
    </>
  );
}

// ─── Stripe Elements modal ────────────────────────────────────────────────

function PaymentModal({
  clientSecret,
  stripeAccountId,
  amountLabel,
  invoiceNumber,
  onClose,
  onSuccess,
}: {
  clientSecret: string;
  stripeAccountId: string;
  amountLabel: string;
  invoiceNumber: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const stripePromise = useMemo(() => loadStripeForAccount(stripeAccountId), [stripeAccountId]);
  return (
    <div style={backdropStyle} role="dialog" aria-modal="true" aria-label="Pay invoice">
      <div style={modalStyle}>
        <header
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            flexWrap: 'wrap',
            gap: '0.5rem',
            marginBottom: '0.75rem',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>
            Pay invoice {invoiceNumber}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '1.25rem',
              cursor: 'pointer',
              color: 'var(--text-muted)',
            }}
          >
            ×
          </button>
        </header>
        <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', marginTop: 0 }}>
          Total <strong style={{ color: 'var(--text)' }}>{amountLabel}</strong>. Card payment is
          processed by the auction house via Stripe.
        </p>
        <Elements
          stripe={stripePromise}
          options={{ clientSecret, appearance: { theme: 'stripe' } }}
        >
          <PayForm onSuccess={onSuccess} />
        </Elements>
      </div>
    </div>
  );
}

function PayForm({ onSuccess }: { onSuccess: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    setErr(null);
    const res = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });
    if (res.error) {
      setErr(res.error.message ?? 'Payment failed.');
      setBusy(false);
      return;
    }
    // redirect: 'if_required' returns without reload on success for most cards.
    onSuccess();
    setBusy(false);
  }

  return (
    <form
      onSubmit={(e) => {
        void handleSubmit(e);
      }}
      style={{ display: 'grid', gap: '0.75rem' }}
    >
      <PaymentElement />
      {err && (
        <div role="alert" style={errorStyle}>
          {err}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
        <Button type="submit" disabled={busy || !stripe}>
          {busy ? 'Processing…' : 'Pay'}
        </Button>
      </div>
    </form>
  );
}

// ─── Styles ────────────────────────────────────────────────────────────────

/*
 * The account sections render inside `AccountLayout`'s content column now
 * (2026-09-03, web design handoff), which already supplies the page gutter and
 * the max width. So this carries no padding and no width of its own — two
 * containers both centring their contents is how a section ends up in a narrow
 * strip down the middle of a column that is already narrow.
 *
 * It is a `<div>`, not a `<main>`: the shell renders the page's one `<main>`,
 * and these were nested inside it even before the rail existed.
 */

const errorStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  borderRadius: 'var(--r-control, 0.375rem)',
  background: 'var(--surface-danger, #fef2f2)',
  color: 'var(--text-danger, #991b1b)',
  border: '1px solid var(--border-danger, #fecaca)',
  marginBottom: '1rem',
};

const savedCardRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.6rem',
  padding: '0.6rem 0.75rem',
  border: '1px solid var(--brand-hairline, var(--border))',
  borderRadius: 'var(--r-tile, 0.5rem)',
  background: 'var(--surface)',
  fontSize: '0.9rem',
  cursor: 'pointer',
};

const linkBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: 'var(--brand-accent)',
  fontSize: '0.85rem',
  cursor: 'pointer',
  padding: 0,
  textAlign: 'left',
};

const backdropStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '1rem',
  zIndex: 50,
};

const modalStyle: React.CSSProperties = {
  background: 'var(--surface)',
  borderRadius: 'var(--r-card, 0.5rem)',
  padding: '1.25rem 1.5rem',
  maxWidth: '28rem',
  width: '100%',
  border: '1px solid var(--brand-hairline, var(--border))',
  boxShadow: 'var(--elev-3)',
};
